import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Phone } from "lucide-react";
import { IMAGES, STEPS, FAQS, PHONE_DISPLAY, PHONE_LINK, EMAIL, EMAIL_LINK, LOGO_WHITE, LOGO_LIGHT, BUSINESS, FOOTER_NAV, FOOTER_SERVICES, scrollToId, openInquiry, EASE } from "@/lib/site";
import { PrimaryButton, Arrow, OutlineButton } from "@/components/site/Buttons";
import { LineReveal, Reveal, ClipImage } from "@/components/site/motion";
import { FieldCanvas } from "@/components/site/Canvases";

const BigPara = ({ children, delay }) => (
  <Reveal delay={delay}>
    <p className="text-base sm:text-lg text-charcoal/80 leading-relaxed">{children}</p>
  </Reveal>
);

const StepsTimeline = () => {
  const [active, setActive] = useState(0);
  return (
    <div id="timeline" data-testid="inquiry-steps" className="mt-32 lg:mt-44 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-36">
          <p className="font-mono text-[11px] tracking-[0.24em] text-charcoal/60 mb-4">THE FIRST STEPS</p>
          <div className="relative h-24 sm:h-36 overflow-hidden">
            <AnimatePresence initial={false}>
              <motion.div
                key={STEPS[active].num}
                initial={{ y: "60%", opacity: 0 }}
                animate={{ y: "0%", opacity: 1 }}
                exit={{ y: "-60%", opacity: 0 }}
                transition={{ duration: 0.55, ease: EASE }}
                data-testid="steps-active-number"
                className="absolute inset-0 font-display font-medium tracking-[-0.03em] leading-none text-6xl sm:text-8xl text-charcoal"
              >
                {STEPS[active].num}
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="mt-6 h-px w-full bg-charcoal/10 relative">
            <motion.div className="absolute left-0 -top-px h-[3px] rounded-full sun-bar" animate={{ width: `${((active + 1) / STEPS.length) * 100}%` }} transition={{ duration: 0.6, ease: EASE }} />
          </div>
        </div>
      </div>
      <div className="lg:col-span-7">
        {STEPS.map((s, i) => (
          <motion.div
            key={s.num}
            data-testid={`step-item-${s.num}`}
            onViewportEnter={() => setActive(i)}
            viewport={{ margin: "-45% 0px -45% 0px" }}
            className={`min-h-[38vh] flex flex-col justify-center border-l-2 pl-8 sm:pl-12 transition-[opacity,border-color] duration-700 relative ${active === i ? "border-leaf opacity-100" : "border-charcoal/10 opacity-50"}`}
          >
            <span className={`absolute -left-[7px] top-1/2 -translate-y-1/2 w-3 h-3 rounded-full transition-colors duration-500 ${active === i ? "bg-sun ring-2 ring-leaf" : "bg-charcoal/20"}`} />
            <p className="font-mono text-xs tracking-[0.24em] text-leaf mb-3">STEP {s.num}</p>
            <h3 className="font-display font-medium tracking-[-0.02em] text-xl sm:text-3xl text-charcoal leading-snug max-w-md">{s.title}</h3>
            <p className="mt-3 text-sm sm:text-base text-charcoal/70 leading-relaxed max-w-md">{s.text}</p>
          </motion.div>
        ))}
        <Reveal y={20} className="mt-10 pl-8 sm:pl-12">
          <PrimaryButton testid="steps-start-inquiry-button" onClick={() => openInquiry()}>DISCUSS MY PROJECT <Arrow /></PrimaryButton>
        </Reveal>
      </div>
    </div>
  );
};

export const Approach = () => (
  <section id="approach" data-testid="approach-section" className="px-6 sm:px-10 lg:px-16 py-16 sm:py-36 max-w-[1700px] mx-auto">
    <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
      <div className="lg:col-span-6">
        <p className="eyebrow mb-5">YOUR LANDSCAPING PROJECT</p>
        <LineReveal
          as="h2"
          className="font-display font-medium tracking-[-0.03em] leading-[1.05] text-3xl sm:text-5xl lg:text-[4rem] text-charcoal"
          lines={[<>You bring the ideas. <span className="font-serif italic font-normal">We help you explore them.</span></>]}
        />
        <div className="mt-10 space-y-6 max-w-xl">
          <BigPara>You do not need to know every plant, material, or measurement before contacting us.</BigPara>
          <BigPara delay={0.08}>Start with what you want to change and how you want the space to feel.</BigPara>
          <BigPara delay={0.16}>A few photos, your location, and your priorities can help make the first conversation useful.</BigPara>
          <Reveal delay={0.3}>
            <p className="font-display font-semibold tracking-[-0.02em] text-2xl sm:text-4xl text-leaf pt-4">Four simple steps to start the conversation.</p>
          </Reveal>
          <Reveal delay={0.36}>
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <PrimaryButton testid="approach-start-inquiry-button" onClick={() => openInquiry()}>START MY PROJECT INQUIRY <Arrow /></PrimaryButton>
              <OutlineButton testid="approach-see-steps-button" onClick={() => scrollToId("timeline")}>SEE THE STEPS</OutlineButton>
            </div>
          </Reveal>
        </div>
      </div>
      <div className="lg:col-span-6 relative">
        <div className="lg:sticky lg:top-28 pb-12">
          <div className="relative">
            <ClipImage image={IMAGES.storyMain} className="aspect-[4/5] w-[82%]" parallax={7} rounded="rounded-[2rem]" sizes="(max-width: 1024px) 82vw, 40vw" />
            <motion.div
              initial={{ opacity: 0, y: 40, rotate: 4 }}
              whileInView={{ opacity: 1, y: 0, rotate: 3 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 1, delay: 0.25, ease: EASE }}
              className="absolute -bottom-12 right-0 w-[52%] rounded-[1.5rem] overflow-hidden border-[6px] border-cream shadow-[0_24px_60px_rgba(17,17,17,0.22)]"
            >
              <img src={IMAGES.storySmall.src} srcSet={IMAGES.storySmall.srcSet} sizes="(max-width: 1024px) 52vw, 25vw" alt={IMAGES.storySmall.alt} loading="lazy" className="w-full aspect-square object-cover" />
            </motion.div>
            <div className="absolute -top-4 right-4 sm:right-10 rotate-[-4deg]">
              <span className="inline-block font-mono text-[10px] sm:text-[11px] tracking-[0.2em] text-leaf border border-leaf/40 rounded-full px-4 py-2 bg-cream/90 backdrop-blur">DESIGN AROUND YOUR LIFE</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <StepsTimeline />
  </section>
);

export const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" data-testid="faq-section" className="px-6 sm:px-10 lg:px-16 py-16 sm:py-32 max-w-[1200px] mx-auto">
      <LineReveal
        as="h2"
        className="font-display font-medium tracking-[-0.03em] leading-[1.05] text-3xl sm:text-5xl text-charcoal mb-12 sm:mb-16"
        lines={[<>Frequently asked <span className="font-serif italic font-normal">questions.</span></>]}
      />
      <div className="border-b border-charcoal/10">
        {FAQS.map((f, i) => (
          <Reveal key={i} delay={0.05 * i}>
            <div className="border-t border-charcoal/10">
              <button
                data-testid={`faq-question-${i + 1}`}
                onClick={() => setOpen(open === i ? -1 : i)}
                aria-expanded={open === i}
                className="w-full flex items-center justify-between gap-6 py-6 sm:py-8 text-left group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf rounded-md"
              >
                <span className={`font-display font-semibold tracking-tight text-lg sm:text-2xl transition-colors duration-300 ${open === i ? "text-leaf" : "text-charcoal group-hover:text-leaf"}`}>{f.q}</span>
                <span className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center transition-[transform,background-color,border-color] duration-500 ${open === i ? "bg-sun border-sun text-charcoal rotate-45" : "border-charcoal/20 text-charcoal"}`}>
                  <Plus className="w-4 h-4" />
                </span>
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.5, ease: EASE }} className="overflow-hidden">
                    <div data-testid={`faq-answer-${i + 1}`} className="pb-8 pr-6 sm:pr-24 space-y-3 max-w-3xl">
                      {f.a.map((p, k) => <p key={k} className="text-sm sm:text-base text-charcoal/75 leading-relaxed">{p}</p>)}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal y={20} className="mt-14 sm:mt-20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
        <div className="max-w-xl">
          <h3 className="font-display font-semibold tracking-tight text-xl sm:text-2xl text-charcoal">Still deciding what your yard could become?</h3>
          <p className="mt-2 text-sm sm:text-base text-charcoal/70 leading-relaxed">Tell us what you would like to change. Start with the ideas you have, even if the details are still taking shape.</p>
        </div>
        <PrimaryButton testid="faq-send-inquiry-button" onClick={() => openInquiry()} className="self-start sm:self-auto">DISCUSS MY PROJECT <Arrow /></PrimaryButton>
      </Reveal>
    </section>
  );
};

export const FinalCta = () => (
  <section id="contact" data-testid="final-cta-section" className="px-3 sm:px-5 lg:px-8 py-6">
    <div className="relative overflow-hidden rounded-[2rem] sm:rounded-[3rem] texture-green noise-overlay">
      <FieldCanvas />
      <div className="absolute inset-0 bg-gradient-to-t from-[#00551D]/60 via-transparent to-transparent" />
      <div className="relative z-10 px-6 sm:px-12 py-24 sm:py-36 text-center">
        <Reveal>
          <img src={LOGO_WHITE} alt={BUSINESS} data-testid="final-cta-logo" className="h-10 sm:h-14 w-auto mx-auto mb-12" />
        </Reveal>
        <Reveal>
          <p className="font-mono text-[11px] sm:text-xs tracking-[0.24em] text-sun mb-6">START YOUR LANDSCAPING PROJECT</p>
        </Reveal>
        <LineReveal
          as="h2"
          className="font-display font-medium tracking-[-0.03em] leading-[1.06] text-4xl sm:text-6xl lg:text-7xl text-white max-w-4xl mx-auto"
          lines={[<>What would make you <span className="font-serif italic font-normal">spend more time outside?</span></>]}
        />
        <Reveal delay={0.2}>
          <p className="mt-7 text-sm sm:text-lg text-white leading-relaxed max-w-2xl mx-auto">
            A patio for gathering, a different garden layout, or a fresh view from the house. Tell us what you have in mind and where you would like help bringing it together.
          </p>
          <p className="mt-4 text-sm sm:text-base text-white/90 leading-relaxed max-w-2xl mx-auto">You can start with a few ideas. Share your location and the changes you are considering.</p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <PrimaryButton testid="final-cta-inquiry-button" onClick={() => openInquiry()} className="px-8 py-4">DISCUSS MY PROJECT <Arrow /></PrimaryButton>
            <a data-testid="final-cta-call-button" href={PHONE_LINK} aria-label={`Call ${BUSINESS} at ${PHONE_DISPLAY}`} className="group inline-flex items-center gap-3 rounded-full border border-sun/70 text-white px-8 py-4 text-sm font-bold tracking-wide hover:bg-sun hover:text-charcoal transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sun focus-visible:ring-offset-2 focus-visible:ring-offset-forest">
              <Phone className="w-4 h-4 transition-transform duration-300 group-hover:rotate-12" />CALL PHIL GRAY
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.4}>
          <div className="mt-14 space-y-1.5">
            <p className="font-mono text-[11px] sm:text-xs tracking-[0.24em] text-white/90">PREFER TO TALK DIRECTLY?</p>
            <p className="font-mono text-[11px] sm:text-xs tracking-[0.14em] text-white">
              <a data-testid="final-cta-phone" href={PHONE_LINK} className="hover:text-sun underline-offset-4 hover:underline">{PHONE_DISPLAY}</a>
              <span className="mx-3 text-sun">|</span>
              <a data-testid="final-cta-email" href={EMAIL_LINK} className="hover:text-sun underline-offset-4 hover:underline break-all">{EMAIL}</a>
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const Footer = () => (
  <footer data-testid="footer" className="mt-6 bg-charcoal text-cream">
    <div className="px-6 sm:px-10 lg:px-16 pt-16 sm:pt-20 pb-32 max-w-[1700px] mx-auto">
      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <img src={LOGO_LIGHT} alt={BUSINESS} data-testid="footer-logo" className="h-10 sm:h-12 w-auto" />
          <p className="mt-6 text-sm text-cream/80 leading-relaxed max-w-xs">
            Phil Gray Landscaping is a family-owned business providing custom landscape design and installation in Dixon and nearby communities. Explore planting, irrigation, patios, and outdoor features for your next project.
          </p>
        </div>
        <div className="md:col-span-2">
          <p className="font-mono text-[11px] tracking-[0.24em] text-sun mb-5">SERVICES</p>
          <ul className="space-y-3 text-sm text-cream/80">{FOOTER_SERVICES.map((s) => <li key={s}>{s}</li>)}</ul>
        </div>
        <div className="md:col-span-2">
          <p className="font-mono text-[11px] tracking-[0.24em] text-sun mb-5">EXPLORE</p>
          <ul className="space-y-3 text-sm">
            {FOOTER_NAV.map((n) => (
              <li key={n.id}>
                <button data-testid={`footer-link-${n.id}`} onClick={() => scrollToId(n.id)} className="text-cream/80 hover:text-sun transition-colors duration-300">{n.label}</button>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-4">
          <p className="font-mono text-[11px] tracking-[0.24em] text-sun mb-5">CONTACT</p>
          <p className="text-sm text-cream/80 leading-relaxed">Based in Dixon, California<br />Serving Dixon and nearby communities</p>
          <div className="mt-4 space-y-1">
            <a data-testid="footer-phone" href={PHONE_LINK} className="block font-display font-bold text-lg text-cream hover:text-sun transition-colors duration-300">{PHONE_DISPLAY}</a>
            <a data-testid="footer-email" href={EMAIL_LINK} className="block text-sm text-cream/80 hover:text-sun transition-colors duration-300">{EMAIL}</a>
          </div>
        </div>
      </div>
      <div className="mt-16 pt-10 border-t border-cream/10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] tracking-[0.24em] text-sun">HAVE A PROJECT IN MIND?</p>
          <p className="mt-2 text-sm text-cream/80 leading-relaxed">Tell us about your yard and what you would like to change.</p>
        </div>
        <PrimaryButton testid="footer-send-inquiry-button" onClick={() => openInquiry()} className="self-start md:self-auto">DISCUSS MY PROJECT <Arrow /></PrimaryButton>
      </div>
      <div className="mt-10 pt-8 border-t border-cream/10">
        <p data-testid="footer-copyright" className="font-mono text-[11px] tracking-[0.18em] text-cream/60">© {new Date().getFullYear()} PHIL GRAY LANDSCAPING</p>
      </div>
    </div>
  </footer>
);
